import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { toast } from "sonner";
import { Upload, X, Loader2, FileCheck2, ScanLine, AlertCircle } from "lucide-react";

/** Iter 24 Sub-Fase C — Componente reutilizavel de upload+OCR+confirmacao.
 *
 * Props:
 *   documentType: string     — chave do DOC_SCHEMAS (ex: 'driving_license_front')
 *   label?: string           — label opcional (usa o do schema por defeito)
 *   endpoint?: string        — '/admin/ocr/analyze' (default) ou '/ocr/analyze' (user)
 *   onCancel: () => void
 *   onConfirm: (result) => void   — { document_type, fields, raw_text, confidence, image_base64, mime_type, file_url? }
 *   uploadOriginal?: boolean — se true, faz upload do binario para /api/uploads e devolve file_url
 */
export default function OCRUploadModal({
  documentType, label, endpoint = "/admin/ocr/analyze",
  onCancel, onConfirm, uploadOriginal = true,
}) {
  const [step, setStep] = useState("upload"); // upload | analyzing | review
  const [preview, setPreview] = useState(null); // dataURL for image preview
  const [b64, setB64] = useState(null);
  const [mime, setMime] = useState("image/jpeg");
  const [result, setResult] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.click?.(); }, []);

  const readFile = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
    if (!okTypes.includes(file.type)) {
      toast.error("Formato não suportado. Use JPG, PNG, WebP ou PDF.");
      return;
    }
    const maxMb = 8;
    if (file.size > maxMb * 1024 * 1024) {
      toast.error(`Ficheiro demasiado grande (>${maxMb}MB)`);
      return;
    }
    const dataUrl = await readFile(file);
    setPreview(file.type.startsWith("image/") ? dataUrl : null);
    const base64 = String(dataUrl).split(",")[1] || "";
    setB64(base64);
    setMime(file.type);
    setStep("analyzing");

    // Upload the original file to Mongo store (parallel to OCR)
    let uploadedUrl = null;
    if (uploadOriginal) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploadedUrl = up.data?.url || null;
        setFileUrl(uploadedUrl);
      } catch (err) {
        // non-blocking
        console.warn("Upload original falhou:", err);
      }
    }

    try {
      const r = await api.post(endpoint, {
        image_base64: base64,
        mime_type: file.type,
        document_type: documentType,
      });
      setResult(r.data);
      setStep("review");
    } catch (err) {
      toast.error(formatApiError(err));
      setStep("upload");
    }
  };

  const updateField = (k, v) => setResult({ ...result, fields: { ...(result.fields || {}), [k]: v } });

  // Iter 24 CR: converte dd/mm/yyyy ou dd-mm-yyyy → yyyy-mm-dd para os inputs date
  const normalizeDate = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
    return s; // let user fix if unparseable
  };

  const confirm = () => {
    onConfirm({
      document_type: result.document_type,
      fields: result.fields || {},
      raw_text: result.raw_text || "",
      confidence: result.confidence || 0,
      image_base64: b64,
      mime_type: mime,
      file_url: fileUrl,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" data-testid="ocr-modal">
      <div className="bg-white border border-slate-200 w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold">OCR · Gemini Vision</div>
            <h3 className="font-display text-xl text-[#0a0f1e]">{label || documentType}</h3>
          </div>
          <button onClick={onCancel}><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={onPick}
          className="hidden"
          data-testid="ocr-file-input"
        />

        <div className="p-6">
          {step === "upload" && (
            <div
              onClick={() => inputRef.current?.click?.()}
              className="border-2 border-dashed border-slate-300 hover:border-[#f59e0b] p-16 text-center cursor-pointer"
              data-testid="ocr-dropzone"
            >
              <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
              <div className="font-semibold text-[#0a0f1e]">Carregar documento</div>
              <div className="text-xs text-slate-500 mt-1">JPG · PNG · WebP · PDF (máx 8MB)</div>
            </div>
          )}

          {step === "analyzing" && (
            <div className="text-center py-16" data-testid="ocr-analyzing">
              <Loader2 className="w-10 h-10 mx-auto text-[#f59e0b] animate-spin" />
              <div className="mt-4 font-semibold text-[#0a0f1e]">A analisar documento…</div>
              <div className="text-xs text-slate-500 mt-1">Gemini Vision está a extrair os campos. Pode demorar 5-15 segundos.</div>
              {preview && <img src={preview} alt="preview" className="mx-auto mt-4 max-h-48 border border-slate-200" />}
            </div>
          )}

          {step === "review" && result && (
            <div data-testid="ocr-review">
              <div className="flex items-start gap-2 border border-emerald-300 bg-emerald-50 p-3 mb-4">
                <FileCheck2 className="w-4 h-4 text-emerald-700 mt-0.5" />
                <div className="text-xs text-emerald-900">
                  Extração concluída · <span className="font-mono font-bold">confiança {(result.confidence * 100).toFixed(0)}%</span> · reveja os campos abaixo antes de confirmar.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {preview && (
                  <div className="border border-slate-200 p-2 bg-slate-50">
                    <img src={preview} alt="doc" className="w-full h-auto max-h-96 object-contain" />
                  </div>
                )}
                <div className="space-y-3">
                  {Object.entries(result.fields || {}).length === 0 ? (
                    <div className="flex items-center gap-2 text-amber-800 text-xs">
                      <AlertCircle className="w-4 h-4" />
                      Nenhum campo detectado. Preencha manualmente ou tente com melhor iluminação.
                    </div>
                  ) : (
                    Object.entries(result.fields || {}).map(([k, v]) => {
                      const isDate = k.includes("date");
                      const val = isDate ? normalizeDate(v) : (v ?? "");
                      return (
                        <div key={k}>
                          <label className="label-caps block mb-1">{k.replace(/_/g, " ")}</label>
                          <input
                            type={isDate ? "date" : "text"}
                            value={val}
                            onChange={(e) => updateField(k, e.target.value)}
                            data-testid={`ocr-field-${k}`}
                            className="input-dt !h-9 font-mono text-sm"
                            placeholder="—"
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {result.raw_text && (
                <details className="mt-4 border border-slate-100 p-3 bg-slate-50/40">
                  <summary className="cursor-pointer text-[11px] uppercase tracking-widest text-slate-600 font-bold">Ver texto bruto extraído</summary>
                  <pre className="mt-2 text-[10px] font-mono text-slate-700 whitespace-pre-wrap max-h-32 overflow-y-auto">{result.raw_text}</pre>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <GhostButton onClick={onCancel} className="flex-1">Cancelar</GhostButton>
          {step === "review" && (
            <>
              <GhostButton onClick={() => { setStep("upload"); setResult(null); setB64(null); setPreview(null); setFileUrl(null); }} data-testid="ocr-retry">
                <ScanLine className="w-4 h-4" /> Re-analisar
              </GhostButton>
              <GoldButton onClick={confirm} className="flex-1 justify-center" data-testid="ocr-confirm">
                <FileCheck2 className="w-4 h-4" /> Confirmar & guardar
              </GoldButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
